import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { Input, SpinnerOverlay, SubmissionButtons, TextArea } from '@components/common';

import { IThresholdPreset } from '@interfaces/alarms/dto/thresholdDTO';
import { IPresetThreshold, IThreshold } from '@interfaces/alarms/threshold';
import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	IPresetThresholdCreateDTO,
	IPresetThresholdUpdateDTO,
} from '@interfaces/kpi/dto/kpiPresetsDTO';
import {
	UpdateOranThresholdPresetHandler,
} from '@components/routes/AdminPanel/KpiThresholdManagement/KpiThresholdsTechnology/interfaces';
import { TSelectOptionType } from '@components/common/Select/Select.types';

import { ERevalidationMode, EValidationMode } from '@enums/validationMode';
import { ESaveTypeId } from '@components/routes/AdminPanel/KpiThresholdManagement/KpiThresholdsTechnology/enums';

import { schema } from './schema';

import styles from './PresetForm.module.scss';

export type TOnRename = (data: Omit<IPresetThresholdUpdateDTO, 'closePresetForm' | 'id'>) => void;
export type TOnSave = (data: Omit<IPresetThresholdCreateDTO, 'data' | 'closePresetForm'>) => void;

interface IPresetFormProps {
	editablePreset: TSelectOptionType<string> & IPresetThreshold | null;
	editOranThresholdPreset: UpdateOranThresholdPresetHandler;
	isLoading: boolean;
	localValuesThresholds: IThreshold[];
	handleClose: () => void;
	onRename: TOnRename;
	onSave: TOnSave;
	presetValue: TSelectOption<string> & IPresetThreshold | null;
	selectedSaveType: ESaveTypeId;
	setIsDirty: Dispatch<SetStateAction<boolean>>;
}

const ThresholdPresetForm = ({
	editOranThresholdPreset,
	editablePreset,
	handleClose,
	isLoading,
	localValuesThresholds,
	onRename,
	onSave,
	presetValue,
	selectedSaveType,
	setIsDirty,
}: IPresetFormProps) => {
	const isUpdateOran = selectedSaveType === ESaveTypeId.saveEdited;
	const isCreate = selectedSaveType === ESaveTypeId.saveNew;

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		setValue,
		setError,
	} = useForm<FieldValues>({
		mode: EValidationMode.all,
		reValidateMode: ERevalidationMode.onChange,
		resolver: yupResolver(schema),
		defaultValues: {
			name: isUpdateOran ? presetValue?.name : editablePreset?.name || '',
			description: isCreate ? '' : editablePreset?.description ?? presetValue?.description,
		},
	});

	const [currentThresholdValues, setCurrentThresholdValues] = useState<IThresholdPreset[]>([]);

	const onSubmit = (values: FieldValues) => {
		const setNameError = (message: string) => setError('name', { message });

		if (editablePreset) {
			onRename({
				name: values.name,
				setNameError,
				description: values.description,
			});
		} else if (isUpdateOran) {
			if (!presetValue?.id) {
				return;
			}

			editOranThresholdPreset({
				id: presetValue.id,
				setNameError,
				data: {
					thresholds: currentThresholdValues,
					description: values.description,
				},
				closePresetForm: handleClose,
			});
		} else {
			onSave({
				name: values.name,
				setNameError,
				description: values.description,
			});
		}
	};

	useEffect(() => setIsDirty(isDirty), [isDirty, setIsDirty]);

	useEffect(() => {
		const thresholds = localValuesThresholds.map(threshold => ({
			kpiId: threshold.kpiId,
			minThreshold: threshold.minThreshold,
			maxThreshold: threshold.maxThreshold,
		}));

		setCurrentThresholdValues(thresholds);
	}, [localValuesThresholds]);

	return (
		<div>
			{isLoading && <SpinnerOverlay />}
			<Input
				errorMessage={errors?.name?.message}
				isReadOnly={isUpdateOran}
				label='Preset Name'
				name='name'
				placeholder='Enter preset name'
				register={register}
				required
				setValue={setValue}
			/>
			<div className={styles.description}>
				<TextArea
					label='Description'
					name='description'
					placeholder='Enter preset description'
					register={register}
					rows={7}
					setValue={setValue}
					withUseForms
				/>
			</div>
			<SubmissionButtons
				negativeHandler={handleClose}
				positiveHandler={handleSubmit(onSubmit)}
				positiveLabel='Save'
			/>
		</div>
	);
};

export default ThresholdPresetForm;
